import React from 'react';

import { IntlProvider } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { RemixThinkingBar } from '@atlassian/malleable-ui-components/remix/remix-thinking-bar';

import { stopRemixThinking } from '../../editor-commands/stop-remix-thinking';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';

const THINKING_WIDGET_KEY = 'remix-thinking-widget';
const THINKING_WIDGET_PLACEHOLDER_KEY = 'remix-thinking-widget-placeholder';

export function renderRemixThinkingPortal({
	api,
	container,
	contentType,
	getIntl,
	isComplete,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	container: HTMLElement;
	contentType?: string;
	getIntl: PMPluginFactoryParams['getIntl'];
	isComplete: boolean;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): void {
	const intl = getIntl();

	const onStopThinking = () => {
		api?.aiExperience?.actions.toggleAiEntryPoints?.(false);

		if (expValEqualsNoExposure('cc_page_experiences_editor_image_generation', 'isEnabled', true)) {
			const remixModalMetadata = api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
			const userPrompt = remixModalMetadata?.userPrompt;
			const onCancel = remixModalMetadata?.onCancel;
			if (onCancel && userPrompt) {
				api?.core.actions.execute(stopRemixThinking({ reopenModal: false }));
				onCancel();
				return;
			}
		}

		api?.core.actions.execute(stopRemixThinking({ reopenModal: true }));
	};

	const onComplete = () => {
		api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		api?.core.actions.execute(stopRemixThinking());
	};

	nodeViewPortalProviderAPI.render(
		() => (
			<IntlProvider
				defaultLocale={intl.defaultLocale || 'en-US'}
				locale={intl.locale || 'en-US'}
				messages={intl.messages}
				formats={intl.formats}
				onError={intl.onError}
			>
				<RemixThinkingBar
					contentType={contentType}
					isComplete={isComplete}
					isRelative
					onCompleteAnimationFinished={onComplete}
					onStop={onStopThinking}
					showProgressBar
				/>
			</IntlProvider>
		),
		container,
		THINKING_WIDGET_KEY,
		undefined,
		true,
	);
}

function createRemixThinkingWidget({
	api,
	contentType,
	getIntl,
	isComplete,
	isPlaceholder,
	nodeViewPortalProviderAPI,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	contentType?: string;
	getIntl: PMPluginFactoryParams['getIntl'];
	isComplete: boolean;
	/**
	 * after the remix thinking is complete, we need to scroll to the widget if it is not in the viewport
	 * so we need to use the widget as a anchor point to scroll to
	 * but we don't need to render the content in the widget
	 */
	isPlaceholder: boolean;
	nodeViewPortalProviderAPI: PortalProviderAPI;
}): HTMLElement {
	const document = getDocument();
	if (!document) {
		throw new Error('Document is required to render the remix thinking widget.');
	}

	const container = document.createElement('div');
	container.className = isPlaceholder
		? `${THINKING_WIDGET_KEY} ${THINKING_WIDGET_PLACEHOLDER_KEY}`
		: THINKING_WIDGET_KEY;
	container.dataset.testid = isPlaceholder ? THINKING_WIDGET_PLACEHOLDER_KEY : THINKING_WIDGET_KEY;

	if (!isPlaceholder) {
		renderRemixThinkingPortal({
			api,
			container,
			contentType,
			getIntl,
			isComplete,
			nodeViewPortalProviderAPI,
		});
	}

	return container;
}

export function getRemixThinkingWidgetContainer(view: EditorView): HTMLElement | null {
	return view.dom.querySelector<HTMLElement>(`.${THINKING_WIDGET_KEY}`);
}

function getPositionAfterDepthOneParent({ doc, to }: { doc: PMNode; to: number }): number {
	const $to = doc.resolve(to);

	return $to.depth >= 1 ? $to.after(1) : to;
}

export function createRemixThinkingWidgetDecoration({
	api,
	contentType,
	doc,
	getIntl,
	isComplete,
	isPlaceholder,
	nodeViewPortalProviderAPI,
	to,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	contentType?: string;
	doc: PMNode;
	getIntl: PMPluginFactoryParams['getIntl'];
	isComplete?: boolean;
	isPlaceholder: boolean;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	to: number;
}): Decoration {
	const decorationKey = isPlaceholder ? THINKING_WIDGET_PLACEHOLDER_KEY : THINKING_WIDGET_KEY;
	const widgetPosition = getPositionAfterDepthOneParent({ doc, to });

	return Decoration.widget(
		widgetPosition,
		() =>
			createRemixThinkingWidget({
				api,
				contentType,
				getIntl,
				isComplete: isComplete ?? false,
				isPlaceholder,
				nodeViewPortalProviderAPI,
			}),
		{
			key: decorationKey,
			// use a low negative side to ensure the decoration is rendered before all other decorations
			// so other decorations update don't cause this decoration to be re-rendered
			side: -999,
			destroy: () => {
				if (!isPlaceholder) {
					nodeViewPortalProviderAPI.remove(THINKING_WIDGET_KEY);
				}
			},
		},
	);
}
